import { useEffect, useMemo, useState } from "react";
import { InputDateProps } from "src/types";

import { easyInsertion, toDate, toDateString } from "./input-date.utils";

export const useInputDate = ({
  name,
  value: datestr = "",
  setFieldValue,
  setFieldTouched,
  closePickerTrigger,
}: InputDateProps) => {
  const [expandedPicker, setExpandedPicker] = useState(false);

  const currentDate = useMemo(() => {
    if (!datestr) return new Date();

    const parsed = toDate(datestr);
    if (isNaN(parsed.getTime())) {
      return new Date();
    }
    return parsed;
  }, [datestr]);

  const fullYear = currentDate.getFullYear();
  const fullMonth = currentDate.getMonth();

  const dayOfWeek = useMemo(() => {
    const firstDayCurrMonth = new Date(fullYear, fullMonth, 1);
    // Lunedì = 0
    return (firstDayCurrMonth.getDay() + 6) % 7;
  }, [fullYear, fullMonth]);

  const days = useMemo(() => {
    const firstDayCurrMonth = new Date(fullYear, fullMonth, 1);
    const firstDayNextMonth = new Date(fullYear, fullMonth + 1, 1);

    const result: Date[] = [];
    for (let d = new Date(firstDayCurrMonth); d < firstDayNextMonth; d.setDate(d.getDate() + 1)) {
      result.push(new Date(d));
    }
    return result;
  }, [fullYear, fullMonth]);

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // Helpers

  const applyDate = (date: Date) => {
    const normalized = toDateString(date);
    setFieldValue?.(name, normalized);
    if (normalized && normalized.length >= 10) {
      setFieldTouched?.(name, true);
    }
  };

  const changeCurrentDate = (updater: (d: Date) => void) => {
    const base = new Date(currentDate);
    updater(base);
    applyDate(base);
  };

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // API usata dal componente

  const updateDate = (date: Date) => {
    applyDate(date);
    setExpandedPicker(false);
  };

  const togglePicker = () => {
    setExpandedPicker((x) => !x);
  };

  const prevYear = () =>
    changeCurrentDate((d) => d.setFullYear(d.getFullYear() - 1));
  const nextYear = () =>
    changeCurrentDate((d) => d.setFullYear(d.getFullYear() + 1));

  const prevTenYear = () =>
    changeCurrentDate((d) => d.setFullYear(d.getFullYear() - 10));
  const nextTenYear = () =>
    changeCurrentDate((d) => d.setFullYear(d.getFullYear() + 10));

  const prevMonth = () =>
    changeCurrentDate((d) => d.setMonth(d.getMonth() - 1));
  const nextMonth = () =>
    changeCurrentDate((d) => d.setMonth(d.getMonth() + 1));

  ////////////////////////////////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setExpandedPicker(false);
  }, [closePickerTrigger]);

  // Normalizza la stringa in input (easyInsertion) + touched
  useEffect(() => {
    if (!datestr) return;

    const normalized = easyInsertion(datestr);

    if (normalized !== datestr) {
      setFieldValue?.(name, normalized);
    }

    if (normalized.length >= 10) {
      setFieldTouched?.(name, true);
    }
  }, [datestr, name, setFieldValue, setFieldTouched]);

  ////////////////////////////////////////////////////////////////////////////////////////////////////

  return {
    days,
    expandedPicker,
    currentDate,
    fullYear,
    fullMonth,
    dayOfWeek,
    updateDate,
    togglePicker,
    prevYear,
    nextYear,
    prevTenYear,
    nextTenYear,
    prevMonth,
    nextMonth,
  };
};
